import React, { useEffect, useRef, useMemo } from "react";
import ApexCharts from "apexcharts";

const ProgressCharts = ({
  title,
  mainLabel,
  categories,
  mainValue,
  totalValue,
  primaryColor = "#167F94",
  secondaryColor = "#A8D1DA",
  primaryLegendLabel,
  secondaryLegendLabel,
  icon,
  legendPosition = "bottom",
  footertitle,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const percentage = useMemo(() => {
    if (totalValue === 0) return 0;
    return ((mainValue / totalValue) * 100).toFixed(0);
  }, [mainValue, totalValue]);

  // The visual bar is a 0–totalValue progress scale. API values can be above
  // that scale (for example 4498%), so never pass a negative remainder to
  // ApexCharts: negative stacked values are drawn on the left of zero.
  const filledValue = useMemo(
    () =>
      Math.min(Math.max(Number(mainValue) || 0, 0), Number(totalValue) || 0),
    [mainValue, totalValue],
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        sparkline: { enabled: true },
        height: "100%",
        width: "100%",
      },
      series: [
        { name: primaryLegendLabel, data: [filledValue] },
        { name: secondaryLegendLabel, data: [totalValue - filledValue] },
      ],
      colors: [primaryColor, secondaryColor],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "80%",
          borderRadius: 4,
        },
      },
      xaxis: {
        categories: [mainLabel],
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 0 ? `${percentage}%` : "",
        style: {
          fontSize: "12px",
          fontWeight: "bold",
          colors: ["#FFF"],
        },
        offsetX: 0,
        offsetY: 0,
      },
      legend: { show: false },
      tooltip: { enabled: false },
      grid: { show: false },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            plotOptions: {
              bar: {
                barHeight: "70%",
              },
            },
            dataLabels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                barHeight: "75%",
              },
            },
            dataLabels: {
              style: {
                fontSize: "11px",
              },
            },
          },
        },
        {
          breakpoint: 1024,
          options: {
            plotOptions: {
              bar: {
                barHeight: "80%",
              },
            },
            dataLabels: {
              style: {
                fontSize: "13px",
              },
            },
          },
        },
      ],
    }),
    [
      mainValue,
      totalValue,
      filledValue,
      percentage,
      primaryColor,
      secondaryColor,
      mainLabel,
      primaryLegendLabel,
      secondaryLegendLabel,
    ],
  );

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = new ApexCharts(chartRef.current, chartOptions);
        chartInstance.current.render();
      } else {
        chartInstance.current.updateOptions(chartOptions);
      }
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartOptions]);

  return (
    <div className="w-full max-w-full">
      {/* Container with flexible responsive layout */}
      <div className="flex items-center justify-between gap-3 lg:gap-5 w-full">
        {/* Title Section */}
        {title && (
          <div className="flex-shrink-0 w-full sm:w-auto">
            <p className="font-calibri text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-2 sm:mb-0 sm:mt-2 break-words">
              {title}
            </p>
          </div>
        )}

        {/* Chart Section */}
        <div className="flex-grow min-w-0">
          <div
            ref={chartRef}
            className="w-full h-16 sm:h-20 md:h-24 lg:h-28 xl:h-32 min-h-[64px]"
            style={{
              margin: 0,
              padding: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          />
        </div>

        {/* Legend Section */}
        <div
          className={`flex-shrink-0 ${
            legendPosition === "right"
              ? "flex flex-col"
              : "flex flex-wrap gap-x-3"
          } gap-1 sm:gap-2 min-w-[140px] max-w-[220px]`}
        >
          <div className="flex items-start gap-1 sm:gap-2">
            <div
              className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 flex-shrink-0 rounded-sm mt-0.5"
              style={{ backgroundColor: primaryColor }}
            ></div>
            <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 flex-1 leading-normal">
              {primaryLegendLabel}
            </span>
          </div>
          <div className="flex items-start gap-1 sm:gap-2">
            <div
              className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 flex-shrink-0 rounded-sm mt-0.5"
              style={{ backgroundColor: secondaryColor }}
            ></div>
            <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 flex-1 leading-normal">
              {secondaryLegendLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Footer (if exists) */}
      {footertitle && (
        <div className="mt-2 sm:mt-3">
          <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 break-words">
            {footertitle}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgressCharts;
